// DiasDaSemana (extensão do Chip §5.4; mockups painel-agenda-treino.html §6 e painel-criar-treino-dias.html §5):
// os 7 dias em grade de 7 colunas iguais (44 px em 390, 40 px em 360; área de toque 48). Cada dia é um Chip
// `papel="checkbox"`; marcado = cheio em tinta e semibold, sem ícone (cheio × vazado já é diferença de forma).
// Rótulos "Seg … Dom" (nunca uma letra só: "S" e "Q" repetem). Setas movem o foco, Espaço marca.
import { forwardRef, useImperativeHandle, useRef, useState, type KeyboardEvent } from "react";
import { cn } from "../lib/cn";
import { Chip } from "./Chip";

/** ISO: índice 0 = segunda (dia 1) … 6 = domingo (dia 7). */
export const DIAS_SEMANA_CURTOS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"] as const;
export const DIAS_SEMANA_LONGOS = [
  "segunda-feira",
  "terça-feira",
  "quarta-feira",
  "quinta-feira",
  "sexta-feira",
  "sábado",
  "domingo",
] as const;

export interface PropsDiasDaSemana {
  /** Dias marcados, ISO (1 = segunda … 7 = domingo). */
  valor: readonly number[];
  aoMudar: (dias: number[]) => void;
  /** Nome do grupo para o leitor de tela ("Dias sugeridos do treino A"), quando não há rótulo visível. */
  rotulo?: string;
  /**
   * Id do rótulo VISÍVEL que nomeia o grupo (`aria-labelledby`). Use em vez de fieldset/legend + `rotulo`, que fariam
   * o leitor de tela anunciar o mesmo nome duas vezes.
   */
  rotuloId?: string;
  /** Único erro da agenda ("Dias fixos" sem nenhum dia): os chips vazios ganham a borda de erro. */
  erro?: boolean;
  desabilitado?: boolean;
  className?: string;
}

export interface ControleDiasDaSemana {
  /** Leva o foco ao primeiro dia (erro de validação: "foco no primeiro chip"). */
  focar: () => void;
}

export const DiasDaSemana = forwardRef<ControleDiasDaSemana, PropsDiasDaSemana>(function DiasDaSemana(
  { valor, aoMudar, rotulo, rotuloId, erro, desabilitado, className },
  ref,
) {
  const botoes = useRef<(HTMLButtonElement | null)[]>([]);
  const [focavel, setFocavel] = useState(0);
  useImperativeHandle(ref, () => ({
    focar: () => {
      setFocavel(0);
      botoes.current[0]?.focus();
    },
  }));

  const alternar = (dia: number) =>
    aoMudar(valor.includes(dia) ? valor.filter((d) => d !== dia) : [...valor, dia].sort((a, b) => a - b));

  const mover = (e: KeyboardEvent, i: number) => {
    const delta =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? 1
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? -1
          : 0;
    const alvo = e.key === "Home" ? 0 : e.key === "End" ? 6 : delta ? (i + delta + 7) % 7 : null;
    if (alvo === null) return;
    e.preventDefault();
    setFocavel(alvo);
    botoes.current[alvo]?.focus();
  };

  return (
    <div
      role="group"
      aria-label={rotuloId ? undefined : rotulo}
      aria-labelledby={rotuloId}
      aria-invalid={erro || undefined}
      className={cn("grid grid-cols-7 gap-1.5", className)}
    >
      {DIAS_SEMANA_CURTOS.map((curto, i) => {
        const dia = i + 1;
        const marcado = valor.includes(dia);
        return (
          <Chip
            key={dia}
            ref={(el) => {
              botoes.current[i] = el;
            }}
            papel="checkbox"
            ativo={marcado}
            desabilitado={desabilitado}
            aria-label={DIAS_SEMANA_LONGOS[i]}
            tabIndex={i === focavel ? 0 : -1}
            onFocus={() => setFocavel(i)}
            onKeyDown={(e) => mover(e, i)}
            aoClicar={() => alternar(dia)}
            className={cn("w-full justify-center px-0", marcado ? "font-semibold" : erro && "border-erro")}
          >
            {curto}
          </Chip>
        );
      })}
    </div>
  );
});
